import type { BrandSpecials } from '@iheartjane/data-access';
import type { SmartSort } from '@iheartjane/dm-sdk';
import { FLAGS } from '@iheartjane/feature-flags';
import { WizardBanner } from '@iheartjane/menu';
import { Flex, Typography } from '@iheartjane/reefer';
import { useMemo } from 'react';

import { WizardCTA } from '@jane/bloom/components/bloomWizardModal';
import { DMMenuRow } from '@jane/bloom/components/menuRow/dmMenuRow';
import { ProductGrid } from '@jane/bloom/components/productListingPage/productGrid';
import { useRootLoaderData } from '@jane/bloom/hooks/useRootLoaderData';
import { useShowInlineWizardCta } from '@jane/bloom/hooks/useShowInlineWizardCta';
import { useSkeletonLoader } from '@jane/bloom/hooks/useSkeletonLoader';
import type { WizardOpenSource } from '@jane/bloom/services/tracking/Events';
import { WIZARD_SOURCE } from '@jane/bloom/services/tracking/Events';
import type { CustomLanguage } from '@jane/bloom/types/schemas/bloom/customLanguage';
import type {
  StoreSpecial,
  StoreSpecials,
} from '@jane/bloom/types/schemas/marketplace/storeSpecials';
import type { NavigationCategory } from '@jane/bloom/types/schemas/roots/product';
import { formatProductCount } from '@jane/bloom/utils/strings/formatProductCount';
import type { JaneGold } from '@jane/bloom/viewModels/productDetail';
import type { AlgoliaProduct } from '@jane/search/types';

import styles from './categoryTab.module.css';

type DefaultCategoryTabProps = {
  brandSpecials: BrandSpecials;
  category: NavigationCategory | undefined;
  customCategoryLabels: Record<string, string>;
  customLanguage: CustomLanguage;
  dynamicPlacement?: SmartSort<AlgoliaProduct>;
  enabledSpecialsForRow: StoreSpecial[];
  featuredTab?: boolean;
  janeGold: JaneGold;
  menuTopRowInstance?: SmartSort<AlgoliaProduct>;
  openWizard?: (source: WizardOpenSource) => void;
  pauseRefetching: () => void;
  preloadedProductIds: number[];
  rowInstance: SmartSort<AlgoliaProduct> | undefined;
  specials: StoreSpecials;
  unpauseRefetching: () => void;
};

export function CategoryTab({
  brandSpecials,
  category,
  customCategoryLabels,
  customLanguage,
  dynamicPlacement,
  enabledSpecialsForRow,
  featuredTab,
  janeGold,
  menuTopRowInstance,
  openWizard,
  pauseRefetching,
  preloadedProductIds,
  rowInstance,
  specials,
  unpauseRefetching,
}: DefaultCategoryTabProps) {
  const { featureFlags } = useRootLoaderData();
  const showInlineWizardCta = useShowInlineWizardCta();
  const { skeletonLoader } = useSkeletonLoader();

  const hasResults = useMemo(
    () => (rowInstance?.products?.length ?? 0) > 0,
    [rowInstance?.products?.length]
  );

  if (!rowInstance) {
    return <></>;
  }

  if (skeletonLoader) {
    return skeletonLoader;
  }

  return (
    <main className="main">
      {!featuredTab && (
        <div className={styles['categoryTabContainer']}>
          {showInlineWizardCta && !category && openWizard && (
            <div className={styles['wizardBanner']}>
              <WizardBanner
                label={
                  featureFlags[FLAGS.c2BudtenderCta]
                    ? WizardCTA.Budtender
                    : WizardCTA.HelpMeChoose
                }
                onClickStart={() => openWizard(WIZARD_SOURCE.BANNER)}
              />
            </div>
          )}
          <Typography
            color="inherit"
            variant="header-bold"
            className="product-count"
            as="h2"
          >
            {category ? customCategoryLabels[category] : 'Shop all'}
          </Typography>
        </div>
      )}

      {!hasResults && (
        <Flex justifyContent="center" alignItems="center" height="500px">
          <Typography
            color="inherit"
            variant="title-bold"
            data-testid="no-results"
          >
            No Products Available
          </Typography>
        </Flex>
      )}

      {menuTopRowInstance && (
        <DMMenuRow
          pauseRefetching={pauseRefetching}
          unpauseRefetching={unpauseRefetching}
          brandSpecials={brandSpecials}
          menuTopRow={menuTopRowInstance}
          customLanguage={customLanguage}
          specials={enabledSpecialsForRow}
          janeGold={janeGold}
          embedInProductGrid={true}
        />
      )}

      {hasResults && (
        <Typography color="grays-mid" variant="body" className="product-count">
          {formatProductCount(rowInstance?.totalProducts)}
        </Typography>
      )}

      <ProductGrid
        brandSpecials={brandSpecials}
        customLanguage={customLanguage}
        dynamicPlacement={dynamicPlacement}
        key={category}
        preloadedProductIds={preloadedProductIds}
        rowInstance={rowInstance}
        specials={specials}
        janeGold={janeGold}
      />
    </main>
  );
}
